import clsx from 'clsx'
import React, { useCallback, useEffect, useId, useRef, useState } from 'react'

const OptionItem = ({ isSelected, label, value, onSelect }) => {
  return (
    <li aria-selected={isSelected} onClick={onSelect} className="form-builder-select-field-dropdown-content-item ">
      <span style={{ fontWeight: isSelected ? 500 : 300 }} className="dome-p1 dome-color-darkest-grey dome-text-w300">
        {label}
      </span>
    </li>
  )
}

const SelectField = ({
  label,
  value,
  required,
  options,
  optionKey,
  onChange,
  placeholder,
  multi,
  error,
  className,
}) => {
  const id = useId()
  const ref = useRef()
  const [openDropDown, setOpenDropDown] = useState(false)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpenDropDown(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const getIdentifier = useCallback(
    (val) => {
      if (optionKey && typeof val === 'object' && val !== null) return val[optionKey]
      return val
    },
    [optionKey]
  )

  const handleOptionClick = useCallback(
    (optionValue) => {
      if (multi) {
        const currentValues = Array.isArray(value) ? value : []
        const optionIdentifier = getIdentifier(optionValue)

        const existingIndex = currentValues.findIndex((v) => getIdentifier(v) === optionIdentifier)

        const newValue =
          existingIndex > -1
            ? currentValues.filter((_, index) => index !== existingIndex)
            : [...currentValues, optionValue]

        onChange(newValue)
      } else {
        onChange(optionValue)
      }
    },
    [multi, value, onChange, getIdentifier]
  )

  const getDisplayValue = () => {
    if (multi) {
      if (!Array.isArray(value) || value.length === 0) {
        return <span className="dome-p1 dome-color-med-grey">{placeholder}</span>
      }
      return (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, flexWrap: 'wrap' }} className="dome-d-flex dome-gap6">
          {value.map((val, index) => {
            const option = options.find((o) => getIdentifier(o) === getIdentifier(val))
            return (
              <li key={getIdentifier(val)} className="dome-p1 dome-color-darkest-grey">
                {option ? option.label : val}
                {index !== value.length - 1 && `,`}
              </li>
            )
          })}
        </ul>
      )
    }

    const selectedOption = options.find((o) => getIdentifier(o) === getIdentifier(value))
    if (selectedOption) {
      return <span className="dome-p1 dome-color-darkest-grey">{selectedOption.label ?? selectedOption}</span>
    }

    return <span className="dome-p1 dome-color-med-grey">{placeholder}</span>
  }

  return (
    <div ref={ref} className={clsx('form-builder-select-field dome-d-flex dome-flex-column', className)}>
      {label && (
        <label htmlFor={id} style={{ margin: 0, width: 'fit-content' }} className="dome-p2 dome-color-med-grey">
          {label} {required && '*'}
        </label>
      )}
      <div onClick={() => setOpenDropDown((pre) => !pre)} className="form-builder-select-field-content">
        <div className="dome-flex-1">{getDisplayValue()}</div>
        <i className="fa-regular fa-chevron-down dome-color-med-grey"></i>
        {openDropDown && (
          <div className="form-builder-select-field-dropdown-container">
            <ul className="form-builder-select-field-dropdown-content">
              {options.map((option) => {
                const optionIdentifier = getIdentifier(option)
                const isSelected = multi
                  ? Array.isArray(value) && value.some((v) => getIdentifier(v) === optionIdentifier)
                  : getIdentifier(value) === optionIdentifier

                return (
                  <OptionItem
                    key={optionIdentifier}
                    isSelected={isSelected}
                    value={optionIdentifier}
                    label={option.label ?? option}
                    onSelect={() => handleOptionClick(option)}
                  />
                )
              })}
            </ul>
          </div>
        )}
      </div>
      {error && (
        <div style={{ paddingLeft: 12, paddingTop: 3 }} className="dome-d-flex dome-align-center dome-gap3">
          <i className="fa-light fa-circle-exclamation dome-color-red-alert"></i>
          <span className="dome-p3 dome-color-red-alert">{error}</span>
        </div>
      )}
    </div>
  )
}

export default SelectField
